import { ExportOutlined, UserOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import { useWindowSize } from 'hooks';
import { Link } from 'react-router-dom';

import { ISSUE_FILTER_OPTIONS } from './issue-status';
import IssueStatusDropdown from './issue-status-dropdown';
import NoteModal from './note-modal';
import TableActions from './table-actions';

/**
 * Unified column definitions for the article relation table.
 * Uses fields from the unified relations API response.
 * @param {Object} params
 * @param {boolean} params.isModal - Whether the table is rendered inside a modal (hides actions column)
 * @param {Object} params.filters - Current filter state from useFilters, used to set filteredValue and sortOrder on columns
 */
function useColumn({ isModal, filters = {} } = {}) {
  const { width } = useWindowSize();
  /** Sütun sabitleme yalnızca geniş ekranlarda uygulanır; küçük ekranlarda tüm sütunlar akar */
  const fixedLeft = width > 575 ? 'left' : null;
  const fixedRight = width > 575 ? 'right' : null;

  /** @param {string} field - Returns Ant Design sortOrder for the given field based on current filters */
  const getSortOrder = (field) => {
    if (filters._sort !== field) return null;
    return filters._sortDir === 'asc' ? 'ascend' : 'descend';
  };

  const columns = [
    {
      title: 'İlişki Yönü',
      dataIndex: 'direction',
      key: 'direction',
      width: 100,
      fixed: fixedLeft,
      sorter: true,
      sortOrder: getSortOrder('direction'),
      filters: [
        { text: 'Doğrudan', value: 'direct' },
        { text: 'Dolaylı', value: 'indirect' },
      ],
      filterMultiple: false,
      filteredValue: filters.direction ? [filters.direction] : null,
      render: (text) => <Tag color={text === 'direct' ? 'green' : 'orange'}>{text === 'direct' ? 'Doğrudan' : 'Dolaylı'}</Tag>,
    },
    {
      title: 'Madde Adı',
      dataIndex: 'relatedTitle',
      key: 'relatedTitle',
      width: 250,
      fixed: fixedLeft,
      sorter: true,
      sortOrder: getSortOrder('relatedTitle'),
      render: (text, record) => (
        <div
          className="gx-d-flex gx-align-items-center"
          style={{ gap: 6 }}
        >
          <Link to={`/madde-iliskilendirme-yonetimi/duzenle/${record.relatedType}-${record.relatedId}`}>{text}</Link>
          {record.resourceUrl && (
            <a
              href={record.resourceUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExportOutlined style={{ fontSize: 14, color: '#1890ff' }} />
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Ansiklopedi',
      dataIndex: 'relatedType',
      key: 'relatedType',
      width: 140,
      fixed: fixedLeft,
      sorter: true,
      sortOrder: getSortOrder('relatedType'),
      filters: [
        { text: 'DİA', value: 'dia' },
        { text: 'TİA', value: 'tia' },
        { text: 'TEMATİK', value: 'tematik' },
      ],
      filteredValue: filters['relatedType[]'] ? [].concat(filters['relatedType[]']) : null,
      render: (text) => <Tag>{text?.toUpperCase()}</Tag>,
    },
    {
      title: 'Açıklama',
      dataIndex: 'note',
      key: 'note',
      width: 160,
      render: (_, record) => (
        <NoteModal
          note={record.note}
          id={record.itemId}
        />
      ),
    },
    {
      title: 'Sorun Var/Yok',
      dataIndex: 'hasIssue',
      key: 'hasIssue',
      width: 150,
      filters: ISSUE_FILTER_OPTIONS,
      filterMultiple: false,
      filteredValue: filters.hasIssue ? [filters.hasIssue] : null,
      render: (hasIssue, record) => (
        <IssueStatusDropdown
          id={record.itemId}
          hasIssue={hasIssue}
        />
      ),
    },
    {
      title: 'Madde Grubu',
      dataIndex: 'articleGroup',
      key: 'articleGroup',
      width: 150,
      sorter: true,
      sortOrder: getSortOrder('articleGroup'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Madde Türü',
      dataIndex: 'articleType',
      key: 'articleType',
      width: 140,
      sorter: true,
      sortOrder: getSortOrder('articleType'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Sorumlu İlim Heyeti',
      dataIndex: 'committee',
      key: 'committee',
      width: 180,
      sorter: true,
      sortOrder: getSortOrder('committee'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Takdim Cümlesi',
      dataIndex: 'lead',
      key: 'lead',
      width: 180,
      sorter: true,
      sortOrder: getSortOrder('lead'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Künye',
      dataIndex: 'kunya',
      key: 'kunya',
      width: 150,
      sorter: true,
      sortOrder: getSortOrder('kunya'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Doğum Ölüm Tarihi',
      dataIndex: 'lifespan',
      key: 'lifespan',
      width: 170,
      sorter: true,
      sortOrder: getSortOrder('lifespan'),
      render: (text) => text ?? '-',
    },
    {
      title: 'Terk mi',
      dataIndex: 'isAbandoned',
      key: 'isAbandoned',
      width: 120,
      filters: [
        { text: 'Terk', value: true },
        { text: 'Terk Değil', value: false },
      ],
      filterMultiple: false,
      filteredValue: filters.isAbandoned != null ? [filters.isAbandoned] : null,
      render: (value) => value == null ? '-' : (
        <Tag color="blue">{value ? 'Terk' : 'Terk Değil'}</Tag>
      ),
    },
    {
      title: 'Taslak mı',
      dataIndex: 'isDraft',
      key: 'isDraft',
      width: 130,
      filters: [
        { text: 'Taslak', value: true },
        { text: 'Taslak Değil', value: false },
      ],
      filterMultiple: false,
      filteredValue: filters.isDraft != null ? [filters.isDraft] : null,
      render: (value) => value == null ? '-' : (
        <Tag color="orange">{value ? 'Taslak' : 'Taslak Değil'}</Tag>
      ),
    },
    {
      title: 'İptal mi',
      dataIndex: 'isCancelled',
      key: 'isCancelled',
      width: 120,
      filters: [
        { text: 'İptal', value: true },
        { text: 'İptal Değil', value: false },
      ],
      filterMultiple: false,
      filteredValue: filters.isCancelled != null ? [filters.isCancelled] : null,
      render: (value) => value == null ? '-' : (
        <Tag color="red">{value ? 'İptal' : 'İptal Değil'}</Tag>
      ),
    },
    {
      title: 'İlişki Kuran',
      dataIndex: 'createdByName',
      key: 'createdByName',
      width: 180,
      render: (_, record) => {
        return (
          <div>
            <div
              className="gx-d-flex gx-align-items-center"
              style={{ gap: 2 }}
            >
              <UserOutlined />
              <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{record.createdByName ? record.createdByName : '-'}</span>
            </div>
            <span className="gx-text-muted">{record.createdAt ? dayjs(record?.createdAt).format('DD/MM/YYYY HH:mm') : '-'}</span>
          </div>
        );
      },
    },
    {
      title: 'İlişkili Maddeler',
      dataIndex: 'relationsCount',
      key: 'relationsCount',
      width: 150,
      sorter: true,
      sortOrder: getSortOrder('relationsCount'),
      render: (text) => <Tag>{text}</Tag>,
    },
    ...(!isModal
      ? [
          {
            title: 'İşlemler',
            key: 'actions',
            width: 90,
            fixed: fixedRight,
            render: (_, record) => <TableActions id={record.itemId} />,
          },
        ]
      : []),
  ];

  return { columns };
}

export default useColumn;
